import { useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  ArrowLeft,
  ArrowUpDown,
  CheckCircle2,
  XCircle,
  Briefcase,
  AlertTriangle,
  User,
  TrendingUp,
  TrendingDown,
  Download,
  FileSpreadsheet,
  FileImage,
  Loader2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { toast } from "sonner";

export default function CandidateCompare({ candidates, job, open, onClose }) {
  const [sortBy, setSortBy] = useState("score"); // score | experience
  const [downloading, setDownloading] = useState(false);
  const compareContentRef = useRef(null);

  const sortedCandidates = useMemo(() => {
    const sorted = [...candidates];
    if (sortBy === "score") {
      sorted.sort((a, b) => 
        (b.match_score?.overall_score || 0) - (a.match_score?.overall_score || 0)
      );
    } else {
      sorted.sort((a, b) => 
        (b.data?.years_experience || 0) - (a.data?.years_experience || 0)
      );
    }
    return sorted;
  }, [candidates, sortBy]);

  // Download comparison as CSV
  const handleDownloadCSV = () => {
    if (!sortedCandidates.length) return;
    
    const headers = [
      "Ranking",
      "Nombre",
      "Score (%)",
      "Nivel de Match",
      "Años Experiencia",
      "Skills Requeridas (%)",
      "Experiencia (%)",
      "Educación (%)",
      "Skills Detectadas",
      "Fortalezas",
      "Brechas"
    ];
    
    const rows = sortedCandidates.map((c, index) => {
      const ms = c.match_score || {};
      const analysis = ms.analysis || {};
      return [
        index + 1,
        c.data?.name || "Desconocido",
        ms.overall_score || 0,
        ms.match_level || "Sin evaluar",
        c.data?.years_experience || 0,
        ms.required_skills_score || 0,
        ms.experience_score || 0,
        ms.education_score || 0,
        (c.data?.skills || []).join("; "),
        (analysis.strengths || []).join("; "),
        (analysis.gaps || []).join("; ")
      ];
    });
    
    const csvContent = [
      `Comparación de Candidatos - ${job?.title || "Puesto"}`,
      `Generado: ${new Date().toLocaleString("es-CR")}`,
      "",
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
    ].join("\n");
    
    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `comparacion_candidatos_${job?.title?.replace(/\s+/g, "_") || "puesto"}_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success("Comparación descargada como CSV");
  };

  // Download comparison as PDF - Professional document generation
  const handleDownloadPDF = async () => {
    if (!sortedCandidates.length) return;
    
    setDownloading(true);
    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = margin;
      
      // Helper function to add new page if needed
      const checkNewPage = (requiredSpace) => {
        if (yPos + requiredSpace > pageHeight - 20) {
          pdf.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };
      
      // ===== HEADER =====
      // Green header bar
      pdf.setFillColor(27, 77, 62); // #1B4D3E
      pdf.rect(0, 0, pageWidth, 35, 'F');
      
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Comparación de Candidatos', margin, 18);
      
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.text(job?.title || 'Puesto', margin, 27);
      
      // Date on right
      pdf.setFontSize(9);
      const dateText = `Generado: ${new Date().toLocaleDateString('es-CR', { 
        day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' 
      })}`;
      pdf.text(dateText, pageWidth - margin, 27, { align: 'right' });
      
      yPos = 45;
      
      // ===== SUMMARY STATS =====
      if (stats) {
        pdf.setFillColor(249, 250, 251); // Light gray background
        pdf.roundedRect(margin, yPos, contentWidth, 22, 3, 3, 'F');
        
        const statsWidth = contentWidth / 4;
        pdf.setTextColor(107, 114, 128); // Gray
        pdf.setFontSize(9);
        
        // Candidates count
        pdf.text('Candidatos', margin + 10, yPos + 8);
        pdf.setTextColor(17, 24, 39);
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text(String(candidates.length), margin + 10, yPos + 17);
        
        // Average
        pdf.setTextColor(107, 114, 128);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Promedio', margin + statsWidth + 10, yPos + 8);
        pdf.setTextColor(17, 24, 39);
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${stats.avgScore}%`, margin + statsWidth + 10, yPos + 17);
        
        // Best
        pdf.setTextColor(107, 114, 128);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Mejor Match', margin + (statsWidth * 2) + 10, yPos + 8);
        pdf.setTextColor(16, 185, 129); // Green
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${stats.maxScore}%`, margin + (statsWidth * 2) + 10, yPos + 17);
        
        // Lowest
        pdf.setTextColor(107, 114, 128);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Menor Match', margin + (statsWidth * 3) + 10, yPos + 8);
        pdf.setTextColor(245, 158, 11); // Amber
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${stats.minScore}%`, margin + (statsWidth * 3) + 10, yPos + 17);
        
        yPos += 30;
      }
      
      // ===== CANDIDATE CARDS =====
      sortedCandidates.forEach((candidate, index) => {
        const score = candidate.match_score?.overall_score || 0;
        const analysis = candidate.match_score?.analysis || {};
        const strengths = analysis.strengths || [];
        const gaps = analysis.gaps || [];
        const isTop = score === stats?.maxScore;
        const requiredSkillsScore = candidate.match_score?.required_skills_score || 0;
        const experienceScore = candidate.match_score?.experience_score || 0;
        const educationScore = candidate.match_score?.education_score || 0;
        
        // Calculate card height
        const cardHeight = 65 + (strengths.length > 0 ? 20 : 0) + (gaps.length > 0 ? 20 : 0);
        checkNewPage(cardHeight);
        
        const cardY = yPos;
        
        // Card background
        pdf.setFillColor(255, 255, 255);
        pdf.setDrawColor(229, 231, 235); // Light border
        pdf.roundedRect(margin, cardY, contentWidth, cardHeight - 5, 3, 3, 'FD');
        
        // Top badge for best match
        if (isTop) {
          pdf.setFillColor(16, 185, 129); // Green
          pdf.roundedRect(margin, cardY, 35, 7, 2, 2, 'F');
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(7);
          pdf.setFont('helvetica', 'bold');
          pdf.text('MEJOR MATCH', margin + 3, cardY + 5);
        }
        
        // Ranking number
        pdf.setFillColor(243, 244, 246);
        pdf.circle(margin + 12, cardY + (isTop ? 18 : 14), 8, 'F');
        pdf.setTextColor(75, 85, 99);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text(String(index + 1), margin + 12, cardY + (isTop ? 18 : 14) + 1, { align: 'center' });
        
        // Candidate name
        pdf.setTextColor(17, 24, 39);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        const name = candidate.data?.name || 'Candidato';
        pdf.text(name.length > 35 ? name.substring(0, 35) + '...' : name, margin + 25, cardY + (isTop ? 17 : 13));
        
        // Experience badge
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(107, 114, 128);
        pdf.text(`${candidate.data?.years_experience || 0} años de experiencia`, margin + 25, cardY + (isTop ? 24 : 20));
        
        // Score circle on right
        const scoreX = pageWidth - margin - 20;
        const scoreY = cardY + 18;
        
        // Score circle color
        if (score >= 75) {
          pdf.setFillColor(16, 185, 129); // Green
        } else if (score >= 50) {
          pdf.setFillColor(245, 158, 11); // Amber
        } else {
          pdf.setFillColor(239, 68, 68); // Red
        }
        pdf.circle(scoreX, scoreY, 12, 'F');
        
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`${score}`, scoreX, scoreY + 2, { align: 'center' });
        
        pdf.setFontSize(6);
        pdf.text('%', scoreX + 8, scoreY - 2);
        
        // Match level below score
        pdf.setTextColor(107, 114, 128);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.text(candidate.match_score?.match_level || 'Sin evaluar', scoreX, scoreY + 18, { align: 'center' });
        
        // Skills section
        let innerY = cardY + (isTop ? 32 : 28);
        
        // Skills
        const skills = (candidate.data?.skills || []).slice(0, 6);
        if (skills.length > 0) {
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(107, 114, 128);
          pdf.text('HABILIDADES:', margin + 5, innerY);
          
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(55, 65, 81);
          const skillsText = skills.join(' • ') + (candidate.data?.skills?.length > 6 ? ` (+${candidate.data.skills.length - 6} más)` : '');
          const skillsLines = pdf.splitTextToSize(skillsText, contentWidth - 60);
          pdf.text(skillsLines[0], margin + 35, innerY);
          innerY += 8;
        }
        
        // Score breakdown
        pdf.setFillColor(249, 250, 251);
        pdf.roundedRect(margin + 5, innerY, contentWidth - 50, 12, 2, 2, 'F');
        
        pdf.setFontSize(7);
        pdf.setTextColor(107, 114, 128);
        const breakdownY = innerY + 8;
        pdf.text(`Skills: ${requiredSkillsScore}%`, margin + 10, breakdownY);
        pdf.text(`Experiencia: ${experienceScore}%`, margin + 45, breakdownY);
        pdf.text(`Educación: ${educationScore}%`, margin + 90, breakdownY);
        
        innerY += 16;
        
        // Strengths
        if (strengths.length > 0) {
          pdf.setFillColor(236, 253, 245); // Light green
          pdf.roundedRect(margin + 5, innerY, contentWidth - 50, 14, 2, 2, 'F');
          
          pdf.setFontSize(7);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(5, 150, 105); // Green
          pdf.text('✓ FORTALEZAS:', margin + 8, innerY + 5);
          
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(7);
          const strengthsText = strengths.slice(0, 2).join(' | ');
          const sLines = pdf.splitTextToSize(strengthsText, contentWidth - 70);
          pdf.text(sLines[0], margin + 8, innerY + 11);
          innerY += 16;
        }
        
        // Gaps
        if (gaps.length > 0) {
          pdf.setFillColor(254, 243, 199); // Light amber
          pdf.roundedRect(margin + 5, innerY, contentWidth - 50, 14, 2, 2, 'F');
          
          pdf.setFontSize(7);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(180, 83, 9); // Amber
          pdf.text('⚠ BRECHAS:', margin + 8, innerY + 5);
          
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(7);
          const gapsText = gaps.slice(0, 2).join(' | ');
          const gLines = pdf.splitTextToSize(gapsText, contentWidth - 70);
          pdf.text(gLines[0], margin + 8, innerY + 11);
        }
        
        yPos = cardY + cardHeight;
      });
      
      // ===== FOOTER =====
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(156, 163, 175);
        pdf.text(
          `Generado con TalentMatcher - www.talentmatcher.net | Página ${i} de ${totalPages}`,
          pageWidth / 2,
          pageHeight - 8,
          { align: 'center' }
        );
      }
      
      // Save PDF
      const fileName = `comparacion_candidatos_${job?.title?.replace(/\s+/g, '_') || 'puesto'}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      toast.success("Comparación descargada como PDF");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Error al generar el PDF");
    } finally {
      setDownloading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 75) return "text-emerald-600 bg-emerald-50";
    if (score >= 50) return "text-amber-600 bg-amber-50";
    return "text-red-600 bg-red-50";
  };

  const getScoreBg = (score) => {
    if (score >= 75) return "bg-emerald-500";
    if (score >= 50) return "bg-amber-500";
    return "bg-red-400";
  };

  // Get best/worst for each category
  const stats = useMemo(() => {
    if (candidates.length === 0) return null;
    
    const scores = candidates.map(c => c.match_score?.overall_score || 0);
    const experiences = candidates.map(c => c.data?.years_experience || 0);
    
    return {
      maxScore: Math.max(...scores),
      minScore: Math.min(...scores),
      maxExp: Math.max(...experiences),
      avgScore: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length),
    };
  }, [candidates]);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="pb-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl font-heading">
                Comparar Candidatos
              </DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">
                {job?.title} • {candidates.length} candidatos seleccionados
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Ordenar por:</span>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="score">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Compatibilidad
                    </div>
                  </SelectItem>
                  <SelectItem value="experience">
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4" />
                      Experiencia
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </DialogHeader>

        {/* Summary Stats */}
        {stats && (
          <div className="py-3 px-1 border-b bg-muted/30 -mx-6 px-6">
            <div className="flex items-center justify-center gap-8 text-sm">
              <div className="text-center">
                <span className="text-muted-foreground">Promedio</span>
                <p className="font-bold text-lg">{stats.avgScore}%</p>
              </div>
              <div className="text-center">
                <span className="text-muted-foreground">Mejor</span>
                <p className="font-bold text-lg text-emerald-600">{stats.maxScore}%</p>
              </div>
              <div className="text-center">
                <span className="text-muted-foreground">Menor</span>
                <p className="font-bold text-lg text-amber-600">{stats.minScore}%</p>
              </div>
            </div>
          </div>
        )}

        {/* Comparison Grid */}
        <div className="flex-1 overflow-auto py-4" ref={compareContentRef}>
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${Math.min(candidates.length, 5)}, minmax(200px, 1fr))` }}>
            {sortedCandidates.map((candidate, index) => {
              const score = candidate.match_score?.overall_score || 0;
              const analysis = candidate.match_score?.analysis || {};
              const strengths = analysis.strengths || [];
              const gaps = analysis.gaps || [];
              const isTop = score === stats?.maxScore;
              
              return (
                <Card 
                  key={candidate.id} 
                  className={`relative ${isTop ? 'ring-2 ring-emerald-500 ring-offset-2' : ''}`}
                >
                  {isTop && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-emerald-500 text-white">
                        Mejor Match
                      </Badge>
                    </div>
                  )}
                  
                  <CardContent className="pt-6 space-y-4">
                    {/* Header */}
                    <div className="text-center">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2">
                        <User className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="font-heading font-semibold truncate" title={candidate.data?.name}>
                        {candidate.data?.name || "Candidato"}
                      </h3>
                      <Link 
                        to={`/candidates/${candidate.id}`}
                        className="text-xs text-primary hover:underline"
                      >
                        Ver detalle →
                      </Link>
                    </div>

                    {/* Score */}
                    <div className="text-center">
                      <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${getScoreColor(score)}`}>
                        <span className="text-2xl font-bold">{score}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {candidate.match_score?.match_level || "Sin evaluar"}
                      </p>
                    </div>

                    {/* Experience */}
                    <div className="flex items-center justify-center gap-2 text-sm">
                      <Briefcase className="w-4 h-4 text-muted-foreground" />
                      <span>{candidate.data?.years_experience || 0} años exp.</span>
                    </div>

                    {/* Skills Match */}
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Skills Clave
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {(candidate.data?.skills || []).slice(0, 4).map((skill, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {skill}
                          </Badge>
                        ))}
                        {(candidate.data?.skills?.length || 0) > 4 && (
                          <Badge variant="outline" className="text-xs">
                            +{candidate.data.skills.length - 4}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Strengths */}
                    {strengths.length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-xs font-medium text-emerald-600 flex items-center gap-1">
                          <TrendingUp className="w-3 h-3" />
                          Fortalezas
                        </p>
                        <ul className="text-xs space-y-1">
                          {strengths.slice(0, 2).map((s, i) => (
                            <li key={i} className="flex items-start gap-1.5">
                              <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0 mt-0.5" />
                              <span className="line-clamp-2">{s}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Gaps */}
                    {gaps.length > 0 && (
                      <div className="space-y-1.5">
                        <p className="text-xs font-medium text-amber-600 flex items-center gap-1">
                          <TrendingDown className="w-3 h-3" />
                          Brechas
                        </p>
                        <ul className="text-xs space-y-1">
                          {gaps.slice(0, 2).map((g, i) => (
                            <li key={i} className="flex items-start gap-1.5">
                              <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0 mt-0.5" />
                              <span className="line-clamp-2">{g}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Score Breakdown Mini */}
                    <div className="pt-2 border-t space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Skills Req.</span>
                        <span className="font-medium">{candidate.match_score?.required_skills_score || 0}%</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Experiencia</span>
                        <span className="font-medium">{candidate.match_score?.experience_score || 0}%</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Educación</span>
                        <span className="font-medium">{candidate.match_score?.education_score || 0}%</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="pt-4 border-t flex flex-col sm:flex-row justify-between items-center gap-3">
          <p className="text-xs text-muted-foreground text-center sm:text-left">
            💡 Tip: Descargá la comparación para compartirla con el equipo
          </p>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2" disabled={downloading}>
                  {downloading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generando...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Descargar
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleDownloadPDF} className="gap-2 cursor-pointer">
                  <FileImage className="w-4 h-4 text-red-500" />
                  <span>Descargar como PDF</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDownloadCSV} className="gap-2 cursor-pointer">
                  <FileSpreadsheet className="w-4 h-4 text-green-500" />
                  <span>Descargar como CSV</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={onClose}>Cerrar</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
